import * as Yup from 'yup';
//project import
import CamelCaseToWords from 'utils/helpers/CamelCaseToWords';
import NumericField from 'components/form-builder/NumericField';
import AsyncWrapper from 'components/form-builder/AsyncWrapper';
import CustomAutoComplete from 'components/form-builder/CustomAutoCompleteField';
import TransferList from 'components/form-builder/TransferListField';
import { FormFieldCustomComponentTypes } from 'constants/FormFieldCustomComponentTypes';
import CustomSelect from './CustomSelect';
import CustomAsyncSelect from './CustomAsyncSelectField';
import { TableActionsTypes } from 'constants/TableActionsTypes';
import PasswordField from './PasswordField';
import TextField from './TextField';
import CustomDateTimePickerField from './CustomDateTimePickerField';
import { Chip } from '@mui/material';

export const GLOBAL_FIELD_PROPS = {
    props: {
        size: 'medium',
    },
    containerProps: {
        p: 2,
    },
};

const nullableFieldSchema = Yup.string().nullable();

const isRequired = arr =>
    arr?.reduce((val, a) => {
        return (a.includes('required') && a.includes(true)) ||
            a[1][1]?.includes('required field')
            ? true
            : val;
    }, false);

const setTitleSuffix = arr => {
    if (!arr) return '';
    return isRequired(arr) ? '*' : '';
};

const setValidationType = arr => {
    // if (!arr) return 'string';
    return isRequired(arr) ? 'string' : nullableFieldSchema;
};

export function FormConfigBuilder(config, entity) {
    let formConfig = config?.map(field => {
        switch (field.componentType) {
            case FormFieldCustomComponentTypes.TextField:
                return Object.assign(
                    {
                        component: FormFieldCustomComponentTypes.TextField,
                        title: CamelCaseToWords(field?.attribute),
                        titleSuffix: setTitleSuffix(field?.validations),
                        col: {
                            // Here you can specify how many Grid columns the field should take for the corresponding breakpoints
                            sm: 6,
                        },
                        ...GLOBAL_FIELD_PROPS,
                        validationType: 'string',
                        props: {
                            ...GLOBAL_FIELD_PROPS.props,
                            inputProps: {
                                readOnly: field?.readOnly,
                                autoComplete: 'off',
                            },
                        },
                        customComponent: TextField,
                    },
                    { ...field, component: 'custom' },
                );
            case FormFieldCustomComponentTypes.CustomNumericField:
                return Object.assign(
                    {
                        title: CamelCaseToWords(field.attribute),
                        titleSuffix: setTitleSuffix(field?.validations),
                        col: {
                            // Here you can specify how many Grid columns the field should take for the corresponding breakpoints
                            sm: 6,
                        },
                        validationType: 'string',
                        ...GLOBAL_FIELD_PROPS,
                        props: {
                            ...GLOBAL_FIELD_PROPS.props,
                            decimalScale: field?.decimalScale || 0,
                            allowNegative: field.allowNegative || false,
                            fixedDecimalScale: true,
                        },
                        customComponent: NumericField,
                    },
                    { ...field, component: 'custom' },
                );
            case FormFieldCustomComponentTypes.CustomDateTimePicker:
                return Object.assign(
                    {
                        title: CamelCaseToWords(field.attribute),
                        titleSuffix: setTitleSuffix(field?.validations),
                        col: {
                            // Here you can specify how many Grid columns the field should take for the corresponding breakpoints
                            sm: 6,
                        },
                        validationType: 'string',
                        ...GLOBAL_FIELD_PROPS,
                        props: {
                            ...GLOBAL_FIELD_PROPS.props,
                        },

                        customComponent: CustomDateTimePickerField,
                    },
                    { ...field, component: 'custom' },
                );

            case FormFieldCustomComponentTypes.CustomAutoComplete:
                return Object.assign(
                    {
                        title: CamelCaseToWords(field.attribute),
                        titleSuffix: setTitleSuffix(field?.validations),
                        col: {
                            // Here you can specify how many Grid columns the field should take for the corresponding breakpoints
                            sm: 6,
                        },
                        ...GLOBAL_FIELD_PROPS,
                        validationType: setValidationType(field?.validations),
                        // eslint-disable-next-line no-unused-vars
                        render: ({
                            execute,
                            status,
                            data,
                            field,
                            methods,
                            f,
                            Component,
                        }) => {
                            const options =
                                status === 'success' && Array.isArray(data)
                                    ? data
                                    : status === 'success' &&
                                      Array.isArray(data?.data)
                                    ? data?.data
                                    : status === 'success' &&
                                      Array.isArray(data?.data?.body)
                                    ? data?.data?.body
                                    : status === 'success' &&
                                      Array.isArray(data?.body)
                                    ? data?.body
                                    : status === 'success' &&
                                      Array.isArray(data?.data?.data)
                                    ? data?.data?.data
                                    : [];
                            field.props = {
                                ...field.props,
                                loading: !(
                                    status === 'success' || status === 'error'
                                ),
                                options: options || [],
                                renderOption: (props, option, state) => {
                                    const uniqueKey =
                                        option.id ||
                                        option[field.optionConfig.value] ||
                                        `${
                                            option[field.optionConfig.key]
                                        }-${options.indexOf(option)}`;
                                    return (
                                        <li {...props} key={uniqueKey}>
                                            {option[field.optionConfig.label]}
                                        </li>
                                    );
                                },
                                renderTags: (tagValue, getTagProps) => {
                                    return tagValue.map((option, index) => {
                                        const uniqueKey =
                                            option.id ||
                                            option[field.optionConfig.value] ||
                                            `${
                                                option[field.optionConfig.key]
                                            }-${index}`;
                                        return (
                                            <Chip
                                                {...getTagProps({ index })}
                                                key={uniqueKey}
                                                label={
                                                    option[
                                                        field.optionConfig.label
                                                    ]
                                                }
                                            />
                                        );
                                    });
                                },
                            };
                            field.onChange = val => {
                                execute({ [field.optionConfig.label]: val });
                            };
                            return (
                                <Component
                                    field={{ ...field, options: options }}
                                    methods={methods}
                                    execute={execute}
                                />
                            );
                        },
                        customComponent: AsyncWrapper(CustomAutoComplete),
                    },
                    { ...field, component: 'custom' },
                );
            case FormFieldCustomComponentTypes.CustomAutoCompleteMultiple:
                return Object.assign(
                    {
                        title: CamelCaseToWords(field.attribute),
                        titleSuffix: setTitleSuffix(field?.validations),
                        col: {
                            // Here you can specify how many Grid columns the field should take for the corresponding breakpoints
                            sm: 6,
                        },
                        ...GLOBAL_FIELD_PROPS,
                        // eslint-disable-next-line no-unused-vars
                        render: ({
                            execute,
                            status,
                            data,
                            field,
                            methods,
                            f,
                            Component,
                        }) => {
                            const options =
                                status === 'success' ? data.data.data : [];
                            field.props = {
                                ...field.props,
                                loading: !(
                                    status === 'success' || status === 'error'
                                ),
                            };
                            return (
                                <Component
                                    field={{ ...field, options }}
                                    methods={methods}
                                />
                            );
                        },
                        customComponent: AsyncWrapper(CustomAutoComplete),
                    },
                    { ...field, component: 'custom' },
                );

            case FormFieldCustomComponentTypes.CustomStandardSelect:
                return Object.assign(
                    {
                        title: CamelCaseToWords(field.attribute),
                        titleSuffix: setTitleSuffix(field?.validations),
                        col: {
                            // Here you can specify how many Grid columns the field should take for the corresponding breakpoints
                            sm: 6,
                        },
                        ...GLOBAL_FIELD_PROPS,
                        // validationType: 'string',
                        // eslint-disable-next-line no-unused-vars
                        render: ({
                            execute,
                            status,
                            data,
                            field,
                            methods,
                            f,
                            Component,
                        }) => {
                            const options = status === 'success' ? data : [];
                            field.props = { ...field.props, onOpen: execute };
                            return (
                                <Component
                                    field={{ ...field, options }}
                                    methods={methods}
                                />
                            );
                        },
                        customComponent: AsyncWrapper(CustomAsyncSelect),
                    },
                    { ...field, component: 'custom' },
                );
            case FormFieldCustomComponentTypes.Select:
                return Object.assign(
                    {
                        title: CamelCaseToWords(field.attribute),
                        titleSuffix: setTitleSuffix(field?.validations),
                        col: {
                            // Here you can specify how many Grid columns the field should take for the corresponding breakpoints
                            sm: 6,
                        },
                        ...GLOBAL_FIELD_PROPS,
                        // validationType: 'string',
                        customComponent: CustomSelect,
                    },
                    { ...field, component: 'custom' },
                );
            case FormFieldCustomComponentTypes.CustomTransferList:
                return Object.assign(
                    {
                        title: CamelCaseToWords(field.attribute),
                        titleSuffix: setTitleSuffix(field?.validations),
                        col: {
                            // Here you can specify how many Grid columns the field should take for the corresponding breakpoints
                            sm: 12,
                        },
                        ...GLOBAL_FIELD_PROPS,
                        // eslint-disable-next-line no-unused-vars
                        render: ({
                            execute,
                            status,
                            data,
                            field,
                            methods,
                            f,
                            Component,
                        }) => {
                            const options =
                                field.options || status === 'success'
                                    ? data?.data?.data
                                    : [];
                            return Component(
                                { ...field, options },
                                methods,
                                f,
                                status,
                            );
                        },
                        customComponent: AsyncWrapper(TransferList),
                    },
                    { ...field, component: 'custom' },
                );

            case FormFieldCustomComponentTypes.Switch:
                return Object.assign(
                    {
                        component: FormFieldCustomComponentTypes.Switch,
                        // label: CamelCaseToWords(field.attribute),
                        titleSuffix: setTitleSuffix(field?.validations),
                        col: {
                            // Here you can specify how many Grid columns the field should take for the corresponding breakpoints
                            sm: 6,
                        },
                        ...GLOBAL_FIELD_PROPS,
                        options: [false, true],
                        idPrefix: 'switch',
                    },
                    field,
                );
            case FormFieldCustomComponentTypes.DisplayText:
                return Object.assign(
                    {
                        component: FormFieldCustomComponentTypes.DisplayText,
                        ...GLOBAL_FIELD_PROPS,
                    },
                    field,
                );
            case FormFieldCustomComponentTypes.PasswordField:
                return Object.assign(
                    {
                        title: CamelCaseToWords(field.attribute),
                        titleSuffix: setTitleSuffix(field?.validations),
                        col: {
                            // Here you can specify how many Grid columns the field should take for the corresponding breakpoints
                            sm: 6,
                        },
                        validationType: 'string',
                        containerProps: {
                            p: 2,
                        },
                        ...GLOBAL_FIELD_PROPS,
                        customComponent: PasswordField,
                    },
                    { ...field, component: 'custom' },
                );

            default:
                return field;
        }
    });
    !!entity &&
        formConfig.unshift({
            component: FormFieldCustomComponentTypes.DisplayText,
            title: `${CamelCaseToWords(
                entity?.ModelFeatureAttribute.CLASS_NAME,
            )}`,
            titleProps: {
                variant: 'h3',
            },
            containerProps: {
                p: 4,
            },
        });
    if (entity?.ModelFeatureAttribute[TableActionsTypes.Deactivate]) {
        formConfig.push({
            component: FormFieldCustomComponentTypes.Switch,
            attribute: 'inactive',
            label: 'Inactive',
            col: {
                // Here you can specify how many Grid columns the field should take for the corresponding breakpoints
                sm: 6,
            },
            ...GLOBAL_FIELD_PROPS,
            options: [false, true],
            idPrefix: 'switch',
        });
    }

    return formConfig;
}
